import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { InputAdornment, OutlinedInput, Stack, Grid2 as Grid, Typography, Box, Link } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AdvancedQuerySaved from "../../../../../Components/AdvancedQuery/AdvancedQuerySaved/advanced-query-saved";
import AdvancedQueryBuilder from "../../../../../Components/AdvancedQuery/AdvancedQueryBuilder/advanced-query-builder";
import { cacheService, searchService, userService } from "../../../../../Core/Api/services";
import { DbSearchAdvancedQueryInfos } from "../../../../../Core/Api/Types/db-search-advanced-query-infos";
import { ObjectLayer } from "../../../../../Core/Api/Types/object-layer";
import { DbAssetObjectInfos } from "../../../../../Core/Api/Types/db-asset-object-infos";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import { GridRowsProp, GridColDef, GridSortModel } from "@mui/x-data-grid";
import { useLocation, useNavigate, useOutletContext } from "react-router";
import AssetStatus from "../../../../../Components/Misc/Status/AssetStatus/asset-status";
import TableCellSingleCover from "../../../../../Components/Table/TableCellSingleCover/table-cell-single-cover";
import { GetMetadataByTagName } from "../../../../../Core/Utils/asset-object-utils";
import { routePaths } from "../../../../../Router/router";
import { TableCellStringExpendDescription } from "../../../../../Components/Table/TableCellStringExpend/table-cell-string-expend";
import { AdvancedQueriesProps } from "../../../../../Components/AdvancedQuery/AdvancedQueries/advanced-queries";
import AssetTable from "../../../../../Components/Table/AssetsTable/assets-table";
import TitleTable from "../../../../../Components/Table/TitlesTable/titles-table";
import { DbMetadataFieldInfos } from "../../../../../Core/Api/Types/db-metadata-field-infos";
import FormSection from "../../../../../Components/Forms/FormSection/form-section";
import AutocompleteSearchCatalog from "../../../../../Components/Search/AutocompleteSearchCatalog/autocomplete-search-catalog";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { GetEnumValueLabelMap, GetObjectStatusLabel } from "../../../../../Utils/helpers";
import { ObjectStatus } from "../../../../../Core/Api/Types/object-status";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { SortingInfos } from "../../../../../Redux/Reducers/redux-slice-filters";
import { SortingType } from "../../../../../Core/Api/Types/sorting-type";
import TableUuids from "../../../../../Components/Table/table-uuids";

export function convertAdvancedQueryToString(query: AdvancedQueriesProps["queries"]) {
  let tmp: any[] = [];
  query.forEach((x) => {
    Object.keys(x)
      .map((key) => {
        if (key !== "start") return x[key];
      })
      .forEach((y) => {
        if (y) tmp.push(y.trim());
      });
  });
  return tmp
    .filter((x) => x !== "success" && x !== "error")
    .join(" ")
    .trim();
}

function splitWithDelimiters(string) {
  const pattern = /(\(|\)|AND|OR)/;
  const splitted = string.split(pattern);
  return splitted.map((elem) => elem.trim()).filter((elem) => elem !== "");
}

function getBeforeAndAfter(string, delimiter) {
  return string.split(delimiter);
}

export function convertStringToAdvancedQuery(query: string, layer: ObjectLayer) {
  let advancedQuery: AdvancedQueriesProps["queries"] = [];
  let metadatas: DbMetadataFieldInfos[] = cacheService
    .GetMetadata()
    .filter((x: DbMetadataFieldInfos) => x.isSearchable && x.tagName !== null);
  let blocks = splitWithDelimiters(query);
  let isOpen = false;

  advancedQuery.push({ start: "Type", condition: "is", end: layer.toString(), color: "success" });
  advancedQuery.push({ operator: "AND" });

  blocks.forEach((b, index) => {
    if (b === "AND") advancedQuery.push({ operator: "AND" });
    else if (b === "OR") advancedQuery.push({ operator: "OR" });
    else if (b === "(" && (!blocks[index - 1] || !blocks[index - 1].includes("IN")))
      advancedQuery.push({ operator: "(" });
    else if (b === "(" && blocks[index - 1] && blocks[index - 1].includes("IN")) {
      isOpen = true;
    } else if (b.includes("IN") || b.includes("NOT IN")) {
    } else if (b === ")" && isOpen) {
      isOpen = false;
    } else if (b === ")" && !isOpen) {
      advancedQuery.push({ operator: ")" });
    } else {
      let before: string = "";
      let after: string = "";
      let condition: string = "";
      let color: "success" | "error" = "success";

      if (b.includes("'IS NOT'")) {
        [before, after] = getBeforeAndAfter(b, "'IS NOT'");
        color = "error";
        condition = "IS-NOT";
      } else if (b.includes(" IS ")) {
        [before, after] = getBeforeAndAfter(b, "IS");
        color = "success";
        condition = "IS";
      } else if (b.includes(" != ")) {
        [before, after] = getBeforeAndAfter(b, "!=");
        color = "error";
        condition = "!=";
      } else if (b.includes(" = ")) {
        [before, after] = getBeforeAndAfter(b, "=");
        color = "success";
        condition = "=";
      } else {
        if (blocks[index - 2] && blocks[index - 2].includes("NOT IN")) {
          before = blocks[index - 2].split("'NOT IN'")[0];
          after = "(" + b + ")";
          color = "error";
          condition = "NOT-IN";
        } else if (blocks[index - 2] && blocks[index - 2].includes("IN")) {
          before = blocks[index - 2].split("IN")[0];
          after = "(" + b + ")";
          color = "success";
          condition = "IN";
        } else {
          console.debug("check me ---> ", b);
        }
      }
      const start = metadatas.find((x) => x.tagName === before)?.label || before;
      advancedQuery.push({
        start: start,
        tagName: start,
        condition: condition,
        end: after,
        color: color,
      });
    }
  });
  return advancedQuery;
}

function InventoryAdvancedQueries() {
  const { t } = useTranslation();
  const [queryListOpacity, setQueryListOpacity] = useState(1);
  const [savedQueriesAdvanced, setSavedQueriesAdvanced] = useState<DbSearchAdvancedQueryInfos[]>([]);
  const [selectedQuery, setSelectedQuery] = useState<string>("");
  const [filter, setFilter] = useState<string>("");
  const [textSearch, setTextSearch] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [results, setResults] = useState<DbAssetObjectInfos[]>([]);
  const [layer, setLayer] = useState<ObjectLayer | null>(null);
  const [titleSearch, setTitleSearch] = useState<string>("");
  const [searchReady, setSearchReady] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [expanded, setExpanded] = useState<boolean>(false);
  const navigate = useNavigate();
  const apiGrid = useGridApiRef();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const location = useLocation();

  // Pagination model
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: cacheService.GetRowsPerPage(location.pathname) ?? 20,
  });
  const [count, setCount] = useState(0);

  // Sorting
  const [sortInfos, setSortInfos] = useState<SortingInfos | undefined>(undefined);
  const [tableSorting, setTableSorting] = useState<GridSortModel>([]);

  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedItems, setSelectedItems } = useOutletContext<{
    setSelectedItems: Dispatch<SetStateAction<DbAssetObjectInfos[]>>;
    selectedItems: DbAssetObjectInfos[];
  }>();

  const onChangeFocus = useCallback((focus: boolean) => {
    setQueryListOpacity(focus ? 0 : 1);
  }, []);

  useEffect(() => {
    userService.GetAvailableSearchQueries().then(function (response) {
      if (response.success && response.queries) {
        setSavedQueriesAdvanced(response.queries);
      }
    });
  }, []);

  useEffect(() => {
    if (searchReady) onClickSearch();
  }, [paginationModel?.pageSize, paginationModel?.page, query, layer, titleSearch, sortInfos]);

  const onClickSearch = () => {
    setIsLoading(true);
    setSelectedQuery(titleSearch);
    setExpanded(false);
    searchService
      .SearchAdvanced({
        hideTitleWithoutAsset: false,
        outputLayer: layer,
        pattern: textSearch === "" ? "*" : textSearch,
        query: query.replace("'IS-NOT'", "'IS NOT'").replace("'NOT-IN'", "'NOT IN'"),
        limit: layer === ObjectLayer.Asset ? paginationModel.pageSize : null,
        page: layer === ObjectLayer.Asset ? paginationModel.page ?? 0 : null,
        sort: sortInfos ?? null,
      })
      .then(function (response) {
        if (response.success && response.results) {
          setCount(response.count ?? 0);
          setResults(response.results);
        } else {
          const messages: string[] = [];
          if (response.message) messages.push(response.message);
          if (response.messages) messages.push(...response.messages);
          if (messages.length) enqueueCustomSnackbar(messages.map((x) => x).join(", "), "error");
          else enqueueCustomSnackbar(t("alert:error.oopsSomethingWentWrong"), "error");
          setResults([]);
          setCount(0);
        }
        setIsLoading(false);
      });
  };

  // Table creation
  const rows: GridRowsProp = results;
  type Row = (typeof rows)[number];

  const contentColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "Id",
      minWidth: CellSize.xsmall,
      flex: CellSize.xsmall,
      renderCell: (data: Row) => (
        <Link onClick={() => navigate(`${routePaths.asset.path}/${data?.row?.id}`)} underline="hover" variant="body1">
          {data.value}
        </Link>
      ),
    },
    {
      field: "",
      headerName: t("menu:item.poster"),
      minWidth: CellSize.xxsmall,
      flex: CellSize.xxsmall,
      hideable: true,
      renderCell: (data: Row) => <TableCellSingleCover coverId={data?.row?.parentTitleId} />,
    },
    {
      field: "parentTitleName",
      headerName: t("table:headerName.parentTitle"),
      minWidth: CellSize.xxlarge,
      flex: CellSize.xxlarge,
      renderCell: (data: Row) => data.value,
    },
    {
      field: "comment",
      headerName: t("common:comment"),
      minWidth: CellSize.xxlarge,
      flex: CellSize.xxlarge,
      valueFormatter: (params: any, row: any) => {
        if (params) {
          return `${row.name + " - " || ""}${row.comment || ""}`;
        } else return "";
      },
      renderCell: (data: Row) => (
        <TableCellStringExpendDescription
          title={data.row["name"]}
          value={data.value}
          width={data.colDef.computedWidth}
        />
      ),
    },
    {
      field: "status",
      headerName: t("common:status"),
      minWidth: CellSize.xxlarge,
      flex: CellSize.xxlarge,
      type: "singleSelect",
      valueOptions: GetEnumValueLabelMap(ObjectStatus, GetObjectStatusLabel),
      renderCell: (data: Row) => (
        <AssetStatus status={data.value} disabled={GetMetadataByTagName(data.row, "isDisabled")?.value} />
      ),
    },
  ];

  const onValueChange = (value: string) => {
    setTextSearch(value);
  };

  return (
    <Stack sx={{ overflowY: "auto", overflowX: "hidden" }}>
      <Stack direction="row" spacing={2} py={2}>
        <Box width="20%">
          <AutocompleteSearchCatalog
            layer={ObjectLayer.Title}
            enableSearchCallback={false}
            onValueChange={onValueChange}
            height={50}
            error={!textSearch.length}
            title={t("page:autocompleteSearchCatalog.title.searchTitleOrEnterAsterisk")}
            context="ExpertSearch"
          />
        </Box>
        <AdvancedQueryBuilder
          sx={{ flex: 1 }}
          onChangeFocus={onChangeFocus}
          onClickSearch={(query, layer, title) => {
            setQuery(query);
            setLayer(layer);
            setTitleSearch(title);
            setSearchReady(true);
          }}
          addAdvancedQuery={setSavedQueriesAdvanced}
          advancedQueries={savedQueriesAdvanced}
          textSearch={textSearch}
        />
      </Stack>
      <FormSection
        title={t("form:section.title.savedQuery", { count: savedQueriesAdvanced.length })}
        expanded={expanded}
        onExpand={() => setExpanded(!expanded)}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ opacity: queryListOpacity, transition: "opacity .3s" }}
        >
          <OutlinedInput
            label={t("common:standard")}
            placeholder={t("field:placeholder.filterByTitle")}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            }
          />
        </Stack>
        <Grid container spacing={2} position="relative" sx={{ opacity: queryListOpacity, transition: "opacity .3s" }}>
          {savedQueriesAdvanced
            .filter((x) => x.name?.toLowerCase().includes(filter.toLowerCase()))
            .map((query) => (
              <Grid size={6} key={query.id}>
                <AdvancedQuerySaved
                  title={query.name || ""}
                  pattern={query.pattern || ""}
                  query={query}
                  id={query.id}
                  onClickSearch={(query, layer, title) => {
                    setQuery(query);
                    setLayer(layer);
                    setTitleSearch(title);
                    setSearchReady(true);
                  }}
                  removeQuery={setSavedQueriesAdvanced}
                  queries={savedQueriesAdvanced}
                />
              </Grid>
            ))}
        </Grid>
      </FormSection>
      <Stack pt={3} flex={1} height={0}>
        {isLoading && <TableSkeleton />}
        {!isLoading &&
          (results && layer !== null ? (
            <>
              <Typography variant="h5" mb={2}>
                {t("page:body.resultsFoundForQuery", {
                  count: count,
                  query: selectedQuery === "" ? titleSearch : selectedQuery,
                })}
              </Typography>
              {(layer === ObjectLayer.Asset && (
                <AssetTable
                  rows={results}
                  paginationModel={paginationModel}
                  onPaginationModelUpdate={(page, pageSize) => setPaginationModel({ page, pageSize })}
                  rowCount={count}
                  sortModel={tableSorting}
                  onSortModelChange={(modelSorting) => {
                    if (modelSorting && modelSorting.length > 0) {
                      setTableSorting(modelSorting);
                      setSortInfos({
                        type: modelSorting[0].field === "durationSMPTE" ? SortingType.Metadata : SortingType.Property, //
                        name: modelSorting[0].field,
                        desc: modelSorting[0].sort === "desc",
                      });
                    } else {
                      setTableSorting([]);
                      setSortInfos(undefined);
                    }
                  }}
                />
              )) ||
                (layer === ObjectLayer.Title && (
                  <TitleTable
                    titles={results}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    extended={false}
                    // paginationModel={paginationModel}
                    // onPaginationModelUpdate={(page, pageSize) => setPaginationModel({ page, pageSize })}
                    // rowCount={count}
                  />
                )) ||
                (layer === ObjectLayer.Content && (
                  <DataTable
                    tableKey={TableUuids.INVENTORY_ADVANCED_QUERIES_CONTENTS}
                    rows={results}
                    columns={contentColumns}
                    columnHeaderHeight={48}
                    rowHeight={56}
                    disableRowSelectionOnClick
                    getRowHeight={() => "auto"}
                    pageSize={paginationModel.pageSize}
                    pagination={true}
                    apiRef={apiGrid}
                    paginationModel={paginationModel}
                    paginationServerSideChangeCallback={(page, pageSize) => setPaginationModel({ page, pageSize })}
                    rowCount={count}
                    paginationMode="server"
                    densityControl
                  />
                ))}
            </>
          ) : (
            <>{titleSearch && <Typography variant="h5">{t("modal:body.noResultsFound")}</Typography>}</>
          ))}
      </Stack>
    </Stack>
  );
}

export default InventoryAdvancedQueries;
